import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon } from "lucide-react";
import type { CanDoAnswer, OnboardingAnswers } from "../../../types/onboarding";
import { canDoAnswers, canDoStatements } from "../../../data/onboarding";
import { StepHeading } from "../StepHeading";
import { spring } from "../../../utils/motion";
interface CanDoStepProps {
  answers: OnboardingAnswers;
  update: (p: Partial<OnboardingAnswers>) => void;
  onDone: () => void;
  targetName: string;
}
const answerTone: Record<CanDoAnswer, string> = {
  0: "bg-sunken",
  1: "bg-accent/70",
  2: "bg-mint"
};
export function CanDoStep({
  answers,
  update,
  onDone,
  targetName
}: CanDoStepProps) {
  const reduce = useReducedMotion() ?? false;
  const firstOpen = answers.canDo.findIndex(v => v === undefined);
  const [i, setI] = useState(() => {
    const filled = answers.canDo.filter(v => v !== undefined).length;
    return filled >= canDoStatements.length ? canDoStatements.length - 1 : firstOpen === -1 ? filled : firstOpen;
  });
  const [dir, setDir] = useState(1);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const answer = (value: CanDoAnswer) => {
    const next = [...answers.canDo];
    next[i] = value;
    update({
      canDo: next
    });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (i < canDoStatements.length - 1) {
        setDir(1);
        setI(i + 1);
      } else {
        onDone();
      }
    }, 220);
  };
  return <div data-id="element-937" data-mp="ivebuqhh8u6g">
      <StepHeading title={`In ${targetName}, can you…`} description="Four quick ones. Go with your gut." data-id="element-938" data-mp="iw7fhil533kd" />

      <div className="flex items-center gap-2" aria-hidden="true" data-id="element-939" data-mp="dv9xp741kbje">
        {canDoStatements.map((_, k) => {
        const v = answers.canDo[k];
        return <span key={k} className={`h-2 flex-1 rounded-full transition-colors duration-200 ${v !== undefined ? answerTone[v] : k === i ? "bg-brand/50" : "bg-sunken"} ${v !== undefined ? "ring-1 ring-line/20" : ""}`} data-id="element-940" data-mp="1pbq6ihl3btxa" data-mpi={k} />;
      })}
      </div>

      <div className="relative mt-6 min-h-[260px]" data-id="element-941" data-mp="vzs0rs4qx7kb">
        <AnimatePresence mode="popLayout" initial={false} custom={dir} data-id="element-942" data-mp="10xla2u1cihot">
          <motion.div key={i} custom={dir} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          x: 40 * dir,
          rotate: 1.5 * dir
        }} animate={{
          opacity: 1,
          x: 0,
          rotate: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          x: -40 * dir,
          rotate: -1.5 * dir,
          transition: {
            duration: 0.16
          }
        }} transition={spring.smooth} className="rounded-panel border-3 border-line bg-surface p-6 shadow-brutal md:p-8" data-id="element-943" data-mp="10vh4m7nstamf">
            <p className="text-sm font-semibold text-ink-2" data-id="element-944" data-mp="3gofsh2b5sgex">
              {i + 1} / {canDoStatements.length}
            </p>
            <p className="mt-2 text-2xl font-bold leading-snug tracking-[-0.01em] text-ink md:text-[28px]" data-id="element-945" data-mp="3llg1nm3b1za9">{canDoStatements[i]}?</p>
            <div className="mt-7 grid grid-cols-3 gap-2.5" role="radiogroup" aria-label={canDoStatements[i]} data-id="element-946" data-mp="394x22azl5kwb">
              {canDoAnswers.map(a => {
              const selected = answers.canDo[i] === a.value;
              return <motion.button key={a.value} type="button" role="radio" aria-checked={selected} whileTap={{
                scale: 0.95
              }} transition={spring.snappy} onClick={() => answer(a.value)} className={`focus-ring rounded-xl border-3 px-2 py-3 text-sm font-bold transition-colors duration-150 md:text-[15px] ${selected ? `border-line ${answerTone[a.value]} text-ink shadow-brutal-sm` : "border-line/30 bg-surface text-ink hover:border-line/70"}`} data-id="element-947" data-mp="3048pzltr5um2">
                    {a.label}
                  </motion.button>;
            })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {i > 0 && <button type="button" onClick={() => {
      setDir(-1);
      setI(i - 1);
    }} className="focus-ring mt-2 flex items-center gap-1.5 rounded-lg text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-948" data-mp="1noi7tljdojc">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-949" data-mp="3nwfy05vflc98" /> Previous
        </button>}
    </div>;
}